// Procedural audio (OXIDE_SPEC.md section 6a: sound is gameplay intel). A self-contained Web Audio engine:
// no samples to ship, every sound is synthesized from noise bursts + oscillators with envelopes. Browsers
// require a user gesture before audio starts, so the context is created lazily and resume() is called on the
// first input. One-shots take an optional world (x,z) so distant events are quieter (footstep carries a few
// metres, a gunshot carries across the map -- the loudness-scaled radius the spec calls for, AUDIO.range in
// config.ts) AND panned left/right by camera-relative azimuth (a StereoPannerNode on every spatial one-shot),
// so direction is legible, not just loudness -- you can tell a gunshot came from behind without turning.
// Ambiance beds (wind + shore) cross-fade by biome each frame.

import { AUDIO } from '../config';

type Surface = 'grass' | 'sand' | 'wood' | 'stone' | 'metal' | 'snow' | 'dirt';
type Tool = 'axe' | 'pickaxe' | 'rock' | 'hammer';

interface Bed { osc: AudioBufferSourceNode; filter: BiquadFilterNode; gain: GainNode; target: number; }

export class AudioEngine {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noise: AudioBuffer | null = null;
  private lx = 0; private lz = 0;       // listener (camera) world position
  private lfacing = 0;                  // listener facing (camera azimuth, radians) -- drives stereo pan
  private wind: Bed | null = null;
  private shore: Bed | null = null;
  private torchT = -1;                  // >=0 while a torch is wielded: schedules crackle pops
  private enabled = true;
  private masterVolume = 0.9;           // desired master gain, REMEMBERED so a pre-resume setMasterVolume (e.g. boot auto-mute) sticks once the node is built

  // Built on the first resume() (a user gesture). Safe to call repeatedly.
  resume(): void {
    if (!this.enabled) return;
    if (!this.ctx) {
      try {
        const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        this.ctx = new Ctx();
      } catch { this.enabled = false; return; }
      this.master = this.ctx.createGain();
      this.master.gain.value = this.masterVolume; // honour any volume set before the gesture (boot auto-mute)
      this.master.connect(this.ctx.destination);
      this.noise = this.makeNoise(2);
      this.startBeds();
    }
    if (this.ctx.state === 'suspended') void this.ctx.resume();
  }

  // x/z is the camera position (not the player's feet) so panning matches what you're LOOKING at, not
  // standing on -- azimuth is the camera's facing (radians, matches IsoCamera.azimuth / player yaw).
  setListener(x: number, z: number, azimuth = 0): void { this.lx = x; this.lz = z; this.lfacing = azimuth; }
  setMasterVolume(v: number): void { this.masterVolume = Math.max(0, Math.min(1, v)); if (this.master) this.master.gain.value = this.masterVolume; }

  // --- one-shots ---------------------------------------------------------------------------------------

  footstep(surface: Surface, x?: number, z?: number): void {
    const g = this.spatial(x, z, AUDIO.range.footstep) * 0.22; // quiet -- footsteps sit well under the action
    if (g <= 0.001) return;
    const p = this.panFor(x, z);
    // Click-free footstep: a LOW body thud (soft, pitch-dropping) + a soft low-pass "scuff" with a GENTLE
    // attack. The clickiness was never the pitch -- it was the ~10ms attack transient on filtered noise; a
    // linear ~25ms attack + lowpass removes the tick entirely. Surface only shifts the scuff cutoff a little.
    const cut = surface === 'metal' ? 1300 : surface === 'stone' ? 1000 : surface === 'snow' ? 1500
      : surface === 'wood' ? 520 : surface === 'sand' ? 380 : 460; // grass/dirt soft + dull
    this.softNoise(cut, surface === 'sand' || surface === 'snow' ? 0.16 : 0.12, g, 0.026, p); // soft scuff, slow attack
    this.softThud(surface === 'wood' ? 72 : surface === 'stone' ? 110 : 90, g * 0.55, p); // low weight, no click
    if (surface === 'metal') this.softNoise(2400, 0.05, g * 0.18, 0.01, p); // a faint metallic shimmer on top
  }

  jump(): void { this.blip(220, 'sine', 0.12, 0.18, 90); }
  uiOk(): void { this.blip(620, 'sine', 0.055, 0.09, 880); }   // #8: a quick rising confirm (open / pick up / deploy / repair)
  uiDeny(): void { this.blip(190, 'square', 0.13, 0.09, 120); } // #8: a low descending buzz (locked / blocked / denied)
  land(surface: Surface, x?: number, z?: number): void { this.footstep(surface, x, z); }

  // A faint far-off footstep for another entity (animal nearby, remote player) -- same shape as a
  // footstep but quieter + a tighter range, so it reads as "something is near", not full detail.
  distantFootstep(surface: Surface, x: number, z: number, range = AUDIO.range.animalFootstep): void {
    const g = this.spatial(x, z, range) * 0.14;
    if (g <= 0.001) return;
    const p = this.panFor(x, z);
    const cut = surface === 'stone' ? 900 : surface === 'sand' ? 340 : 420;
    this.softNoise(cut, 0.1, g, 0.03, p);
    this.softThud(surface === 'stone' ? 100 : 80, g * 0.5, p);
  }

  // The AK crack: a sharp wide-band noise snap + a low thump. Carries far (loud event).
  gunshot(x?: number, z?: number): void {
    const g = this.spatial(x, z, AUDIO.range.gunshot, 0.4) * 0.9;
    if (g <= 0.001) return;
    const p = this.panFor(x, z);
    this.burst(2400, 'highpass', 0.5, 0.07, g, 0.18, p);
    this.burst(700, 'bandpass', 0.8, 0.12, g * 0.8, 0.4, p);
    this.blip(90, 'sine', 0.14, g * 0.7, 45, p);
  }

  // A tool/rock connecting with a node. The node kind tints the sound (woody vs stony).
  toolHit(tool: Tool, node: 'tree' | 'ore' | 'generic', x?: number, z?: number): void {
    const g = this.spatial(x, z, AUDIO.range.toolHit) * 0.7;
    if (g <= 0.001) return;
    const p = this.panFor(x, z);
    if (node === 'tree') { this.burst(520, 'bandpass', 1.6, 0.1, g, 0.45, p); this.blip(150, 'sine', 0.07, g * 0.5, 120, p); }
    else if (node === 'ore') { this.burst(2600, 'bandpass', 4, 0.06, g, 0.4, p); this.blip(tool === 'pickaxe' ? 2200 : 320, 'triangle', 0.08, g * 0.4, undefined, p); }
    else { this.burst(900, 'lowpass', 0.9, 0.09, g, 0.5, p); }
  }

  // A felled tree tipping + crashing: a downward noise whoosh into a broad crash.
  treeFall(x?: number, z?: number): void {
    const g = this.spatial(x, z, AUDIO.range.treeFall) * 0.8;
    if (g <= 0.001 || !this.ctx || !this.noise || !this.master) return;
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource(); src.buffer = this.noise; src.loop = true;
    const f = this.ctx.createBiquadFilter(); f.type = 'lowpass'; f.Q.value = 1;
    f.frequency.setValueAtTime(1400, t); f.frequency.exponentialRampToValueAtTime(220, t + 0.7);
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(g * 0.5, t + 0.15);
    env.gain.exponentialRampToValueAtTime(g * 0.9, t + 0.7); // the crash as it lands
    env.gain.exponentialRampToValueAtTime(0.0001, t + 1.25);
    const out = this.pannedOut(this.panFor(x, z));
    src.connect(f); f.connect(env); env.connect(out);
    src.start(t); src.stop(t + 1.3);
  }

  // A node fully harvested: a gritty crumble (ore) or the tree fall (tree).
  nodeFinish(node: 'tree' | 'ore', x?: number, z?: number): void {
    if (node === 'tree') { this.treeFall(x, z); return; }
    const g = this.spatial(x, z, AUDIO.range.nodeFinish) * 0.7;
    if (g <= 0.001) return;
    const p = this.panFor(x, z);
    this.burst(1500, 'bandpass', 1.2, 0.22, g, 0.7, p);
    this.blip(140, 'sine', 0.12, g * 0.5, 80, p);
  }

  // A door swinging open/closed: a low creak (filtered noise, slow pitch-drop) + a soft latch tick at
  // the end. `closing` drops the pitch slightly so open/close read as distinct without a second sample.
  door(closing: boolean, x?: number, z?: number): void {
    const g = this.spatial(x, z, AUDIO.range.door) * 0.55;
    if (g <= 0.001 || !this.ctx || !this.noise || !this.master) return;
    const p = this.panFor(x, z);
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource(); src.buffer = this.noise; src.loop = true;
    const f = this.ctx.createBiquadFilter(); f.type = 'bandpass'; f.Q.value = 2.2;
    const f0 = closing ? 420 : 340, f1 = closing ? 280 : 520;
    f.frequency.setValueAtTime(f0, t); f.frequency.linearRampToValueAtTime(f1, t + 0.22);
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(g, t + 0.03);
    env.gain.exponentialRampToValueAtTime(0.0001, t + 0.24);
    src.connect(f); f.connect(env); env.connect(this.pannedOut(p));
    src.start(t); src.stop(t + 0.26);
    this.blip(closing ? 260 : 340, 'triangle', 0.05, g * 0.4, undefined, p); // latch tick
  }

  // A build piece placed / upgraded / repaired: a solid knock (tier-tinted) + a short confirm chime.
  // A demolish/crumble is the mirror -- a duller thud with no chime (nothing gained).
  buildPlace(tier: 'twig' | 'wood' | 'stone' | 'metal' | 'reinforced', x?: number, z?: number): void {
    const g = this.spatial(x, z, AUDIO.range.build) * 0.6;
    if (g <= 0.001) return;
    const p = this.panFor(x, z);
    const cut = tier === 'metal' || tier === 'reinforced' ? 2000 : tier === 'stone' ? 1100 : 700;
    this.burst(cut, 'bandpass', 1.4, 0.08, g, 0.35, p);
    this.blip(tier === 'metal' || tier === 'reinforced' ? 900 : tier === 'stone' ? 300 : 200, 'triangle', 0.09, g * 0.5, undefined, p);
  }
  buildDemolish(x?: number, z?: number): void {
    const g = this.spatial(x, z, AUDIO.range.build) * 0.6;
    if (g <= 0.001) return;
    const p = this.panFor(x, z);
    this.burst(600, 'lowpass', 0.8, 0.1, g, 0.5, p);
    this.blip(90, 'sine', 0.12, g * 0.5, 55, p);
  }
  // A base piece crumbling from unpaid upkeep -- a slower, grittier version of demolish (decay, not impact).
  crumble(x?: number, z?: number): void {
    const g = this.spatial(x, z, AUDIO.range.crumble) * 0.55;
    if (g <= 0.001) return;
    const p = this.panFor(x, z);
    this.burst(500, 'lowpass', 0.7, 0.2, g, 0.8, p);
    this.blip(70, 'sine', 0.18, g * 0.4, 40, p);
  }

  // A quick body grunt on taking damage -- always full volume (it's the local player; never spatialized).
  hurt(): void { this.blip(160, 'sawtooth', 0.14, 0.22, 100); this.burst(700, 'lowpass', 1, 0.06, 0.12, 0.3); }

  eat(): void { this.blip(340, 'triangle', 0.07, 0.12, 260); this.softNoise(1800, 0.05, 0.05, 0.008); } // a soft bite/chew tick
  drink(): void { this.softNoise(900, 0.14, 0.09, 0.02); this.blip(220, 'sine', 0.1, 0.06, 180); }       // a light gulp/swallow
  bandage(): void { this.softNoise(2600, 0.1, 0.07, 0.015); this.blip(200, 'sine', 0.08, 0.05, 140); }   // a cloth-tear rasp + a low relief tone
  craftDone(): void { this.blip(520, 'triangle', 0.06, 0.1, 780); this.blip(780, 'sine', 0.08, 0.08, 1040); } // a two-note completion chime, brighter than uiOk

  // Thunder: a deep rolling rumble (low-passed noise, long uneven decay). `closeness` 0 (far) .. 1 (overhead)
  // shapes it -- a sharp loud crack when close, a soft distant roll when far.
  thunder(closeness: number): void {
    if (!this.ctx || !this.noise || !this.master) return;
    const c = Math.max(0, Math.min(1, closeness));
    const g = this.masterVolume * (0.22 + 0.7 * c);
    if (g <= 0.001) return;
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource(); src.buffer = this.noise; src.loop = true;
    const f = this.ctx.createBiquadFilter(); f.type = 'lowpass'; f.Q.value = 0.7;
    f.frequency.setValueAtTime(150 + 160 * c, t); f.frequency.exponentialRampToValueAtTime(55, t + 1.8);
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(g, t + 0.06 + 0.22 * (1 - c)); // sharp crack close, softer far
    env.gain.exponentialRampToValueAtTime(g * 0.45, t + 0.9);            // the roll
    env.gain.exponentialRampToValueAtTime(g * 0.6, t + 1.5);             // a second swell
    env.gain.exponentialRampToValueAtTime(0.0001, t + 2.8);
    src.connect(f); f.connect(env); env.connect(this.master);
    src.start(t); src.stop(t + 2.9);
  }

  torchSetOn(on: boolean): void { this.torchT = on ? 0 : -1; }

  // --- ambiance ----------------------------------------------------------------------------------------

  // Cross-fade the beds toward the focus biome. windAmt is a base wind level (windier in snow/open), shoreAmt
  // rises near the ocean. Eased in tick(). Called each frame from main (cheap; just sets targets).
  setAmbiance(windAmt: number, shoreAmt: number): void {
    if (this.wind) this.wind.target = windAmt;
    if (this.shore) this.shore.target = shoreAmt;
  }

  // Ease the beds + schedule torch crackle. Call each frame.
  tick(dt: number): void {
    if (!this.ctx) return;
    for (const bed of [this.wind, this.shore]) {
      if (!bed) continue;
      const cur = bed.gain.gain.value;
      bed.gain.gain.value = cur + (bed.target - cur) * Math.min(1, dt * 1.5);
    }
    if (this.torchT >= 0) {
      this.torchT -= dt;
      if (this.torchT <= 0) {
        this.torchT = 0.12 + Math.random() * 0.28; // sparser, softer crackle
        this.softNoise(600 + Math.random() * 700, 0.04 + Math.random() * 0.05, 0.05 + Math.random() * 0.05, 0.006); // low soft pop, not a sharp tick
      }
    }
  }

  // --- internals ---------------------------------------------------------------------------------------

  // Distance gain: 1 at the listener, fading to 0 at `radius`. `floor` keeps a loud event audible further.
  private spatial(x?: number, z?: number, radius = 30, floor = 0): number {
    if (x === undefined || z === undefined) return 1;
    const d = Math.hypot(x - this.lx, z - this.lz);
    const k = Math.max(0, 1 - d / radius);
    return Math.max(floor * Math.max(0, 1 - d / (radius * 3)), k * k);
  }

  // Stereo pan (-1 left .. +1 right) for a world point relative to listener facing. undefined x/z (a
  // non-positional sound, e.g. UI) pans center. Behind-listener sounds still pan by left/right, same as
  // real hearing -- only front/back is ambiguous with two ears, which is fine, we're not modelling that.
  private panFor(x?: number, z?: number): number {
    if (x === undefined || z === undefined) return 0;
    const dx = x - this.lx, dz = z - this.lz;
    if (Math.abs(dx) < 0.01 && Math.abs(dz) < 0.01) return 0; // sound at the listener: centered
    const angleToSound = Math.atan2(dx, dz);
    let rel = angleToSound - this.lfacing;
    while (rel > Math.PI) rel -= Math.PI * 2;
    while (rel < -Math.PI) rel += Math.PI * 2;
    return Math.max(-1, Math.min(1, Math.sin(rel))); // sin: full pan at +-90deg, centered ahead/behind
  }

  // A one-shot's gain -> pan -> master chain. Falls back to master directly if panning isn't supported
  // (very old Safari) or pan is ~0, so the common (centered/non-positional) case skips the extra node.
  private pannedOut(pan: number): AudioNode {
    if (!this.master) throw new Error('audio not resumed'); // callers already null-check this.master
    if (Math.abs(pan) < 0.02 || typeof this.ctx!.createStereoPanner !== 'function') return this.master;
    const p = this.ctx!.createStereoPanner(); p.pan.value = pan; p.connect(this.master);
    return p;
  }

  // A filtered noise burst with a fast attack + exponential decay. The workhorse for footsteps/hits/cracks.
  private burst(freq: number, type: BiquadFilterType, q: number, dur: number, gain: number, decay: number, pan = 0): void {
    if (!this.ctx || !this.noise || !this.master || gain <= 0) return;
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource(); src.buffer = this.noise; src.loop = true;
    const f = this.ctx.createBiquadFilter(); f.type = type; f.frequency.value = freq; f.Q.value = q;
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(gain, t + Math.min(0.01, dur * 0.3));
    env.gain.exponentialRampToValueAtTime(0.0001, t + dur * (1 + decay));
    src.connect(f); f.connect(env); env.connect(this.pannedOut(pan));
    src.start(t); src.stop(t + dur * (1 + decay) + 0.02);
  }

  // A short oscillator blip (the tonal body under a hit/footstep). Optional pitch glide to `glideTo`.
  private blip(freq: number, type: OscillatorType, dur: number, gain: number, glideTo?: number, pan = 0): void {
    if (!this.ctx || !this.master || gain <= 0) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator(); o.type = type; o.frequency.setValueAtTime(freq, t);
    if (glideTo) o.frequency.exponentialRampToValueAtTime(glideTo, t + dur);
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0.0001, t);
    env.gain.exponentialRampToValueAtTime(gain, t + 0.005);
    env.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(env); env.connect(this.pannedOut(pan));
    o.start(t); o.stop(t + dur + 0.02);
  }

  // A soft, click-free low-pass "scuff": LINEAR attack (no sharp transient) then a smooth tail. This is the
  // footstep workhorse -- the gentle attack is what removes the tick that `burst` (10ms attack) produced.
  private softNoise(cutoff: number, dur: number, gain: number, attack: number, pan = 0): void {
    if (!this.ctx || !this.noise || !this.master || gain <= 0) return;
    const t = this.ctx.currentTime;
    const src = this.ctx.createBufferSource(); src.buffer = this.noise; src.loop = true;
    const f = this.ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = cutoff; f.Q.value = 0.6;
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(gain, t + attack);          // gentle ramp up = no click
    env.gain.exponentialRampToValueAtTime(0.0001, t + dur + attack);
    src.connect(f); f.connect(env); env.connect(this.pannedOut(pan));
    src.start(t); src.stop(t + dur + attack + 0.02);
  }

  // A low body thud (the weight under a footfall): a sine dropping in pitch with a soft attack. No click.
  private softThud(freq: number, gain: number, pan = 0): void {
    if (!this.ctx || !this.master || gain <= 0) return;
    const t = this.ctx.currentTime;
    const o = this.ctx.createOscillator(); o.type = 'sine';
    o.frequency.setValueAtTime(freq * 1.5, t);
    o.frequency.exponentialRampToValueAtTime(freq * 0.7, t + 0.1);
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0, t);
    env.gain.linearRampToValueAtTime(gain, t + 0.014);           // soft attack
    env.gain.exponentialRampToValueAtTime(0.0001, t + 0.13);
    o.connect(env); env.connect(this.pannedOut(pan));
    o.start(t); o.stop(t + 0.15);
  }

  private startBeds(): void {
    if (!this.ctx || !this.noise || !this.master) return;
    const mk = (type: BiquadFilterType, freq: number, q: number): Bed => {
      const osc = this.ctx!.createBufferSource(); osc.buffer = this.noise!; osc.loop = true;
      const filter = this.ctx!.createBiquadFilter(); filter.type = type; filter.frequency.value = freq; filter.Q.value = q;
      const gain = this.ctx!.createGain(); gain.gain.value = 0;
      osc.connect(filter); filter.connect(gain); gain.connect(this.master!);
      osc.start();
      return { osc, filter, gain, target: 0 };
    };
    this.wind = mk('lowpass', 520, 0.7);   // a soft breathy wind bed
    this.shore = mk('bandpass', 750, 0.6); // a wash for the shoreline
  }

  private makeNoise(seconds: number): AudioBuffer {
    const ctx = this.ctx!;
    const len = Math.floor(ctx.sampleRate * seconds);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    return buf;
  }
}

export type { Surface, Tool };
